// Lernstand aus Bewertungen. Reine Funktionen, ohne Oberfläche und Speicher (getestet in test/learning.test.js).
// Wahrheit sind die Ereignisse (review_events). Daraus entsteht dieselbe Struktur, mit der der Prototyp gerechnet hat (S.units, S.seen, S.days, S.log),
// damit Ablauf und Oberfläche unverändert übernommen werden können.
// Planer: makeScheduler aus platform/sync/sync.mjs (ts-fsrs 5.4.1, Einstellungen wie der Prototyp und Ultreya).
import * as F from 'ts-fsrs';
import { makeScheduler } from '../../platform/sync/sync.mjs';

const SCHEDULERS = {};
export const schedulerFor = (retention = 0.9) => SCHEDULERS[retention] || (SCHEDULERS[retention] = makeScheduler(F, retention));

export const DAY = 864e5;
export const dayStart = t => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
export const nextDayStart = t => { const d = new Date(t); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1); return d.getTime(); };
export const dayKey = t => { const d = new Date(t); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };

export const toStored = c => ({ due: c.due.getTime(), stability: c.stability, difficulty: c.difficulty, elapsed_days: c.elapsed_days, scheduled_days: c.scheduled_days, learning_steps: c.learning_steps, reps: c.reps, lapses: c.lapses, state: c.state, last_review: c.last_review ? c.last_review.getTime() : null });
export const fromStored = f => { const c = Object.assign({}, f, { due: new Date(f.due) }); if (f.last_review === null || f.last_review === undefined) delete c.last_review; else c.last_review = new Date(f.last_review); return c; };

// Wie applyRating im Prototyp: Bewertung g (1 Nochmal, 2 Schwer, 3 Gut, 4 Leicht) zum Zeitpunkt t auf den Zustand u anwenden.
export function rateUnit(u, g, t, sch) {
  const at = new Date(t), card = u.f ? fromStored(u.f) : sch.empty(at);
  const c = sch.next(card, at, g);
  return Object.assign({}, u, { f: toStored(c), s: c.stability, d: c.difficulty, reps: c.reps, lapses: c.lapses, due: c.due.getTime(), last: t });
}

// Reihenfolge wie replay() in sync.mjs: nach Zeitpunkt, bei Gleichstand nach ID
export const byTime = (a, b) => (a.reviewed_at - b.reviewed_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const emptyDerived = () => ({ units: {}, seen: {}, days: {}, log: [], lastT: -Infinity, count: 0 });

// Ein Ereignis anwenden (in zeitlicher Reihenfolge). quiet = Ereignis zählt nicht für Satzstatistik, Lerntage und Trefferquote
// (Schnell-Check „kenne ich“ und Video-Sammlung, wie im Prototyp, der dort nur den Planer aufrief).
export function step(D, e, sch, quiet = false) {
  const t = e.reviewed_at, g = e.rating, u0 = D.units[e.unit_id] || {};
  let u1;
  if (e.direction === 'p') u1 = Object.assign({}, u0, { p: rateUnit(u0.p || {}, g, t, sch), lastSid: e.sentence_id });
  else {
    u1 = Object.assign(rateUnit(u0, g, t, sch), { introAt: u0.introAt || t, lastSid: e.sentence_id });
    // Sprechrichtung nach der ersten gelungenen Abfrage, aber erst ab dem nächsten Tag (wie im Prototyp, Entscheidung 13)
    if (g > 1 && !u1.p && u1.reps - (u1.lapses || 0) >= 1) u1.p = { due: nextDayStart(t) };
  }
  D.units[e.unit_id] = u1;
  if (!quiet) {
    const sn = D.seen[e.sentence_id] || {}; D.seen[e.sentence_id] = { n: (sn.n || 0) + 1, last: t };
    D.log.push({ t, u: e.unit_id, s: e.sentence_id, m: e.direction, g }); if (D.log.length > 500) D.log.shift();
    D.days[dayKey(t)] = (D.days[dayKey(t)] || 0) + 1;
  }
  D.lastT = Math.max(D.lastT, t); D.count += 1;
  return D;
}

// Alles neu berechnen. quietIds: Set der Ereignis-IDs, die nicht in die Statistik eingehen.
export function derive(events, sch, quietIds = new Set()) {
  const D = emptyDerived();
  for (const e of [...events].sort(byTime)) step(D, e, sch, quietIds.has(e.id));
  return D;
}

// Ein neues Ereignis anhängen: schnell, wenn es das jüngste ist, sonst (älteres Ereignis von einem anderen Gerät) alles neu berechnen.
export function append(D, e, events, sch, quietIds) {
  if (e.reviewed_at >= D.lastT) return step(D, e, sch, quietIds.has(e.id));
  return derive(events, sch, quietIds);
}

// Abgeleiteten Stand mit lokalen Angaben zusammenführen:
// intro = Einheiten, die als neu gezeigt, aber noch nicht bewertet wurden ({ uid: Zeitpunkt }); flags = { uid: { known } | { fromCollection } }.
export function withLocal(units, intro = {}, flags = {}) {
  const out = Object.assign({}, units);
  for (const [id, t] of Object.entries(intro)) out[id] = out[id] ? Object.assign({}, out[id], { introAt: Math.min(out[id].introAt || t, t) }) : { introAt: t };
  for (const [id, f] of Object.entries(flags)) if (out[id]) out[id] = Object.assign({}, out[id], f);
  return out;
}
